import React, { useCallback } from "react";
import useSimulationEnMasse from "../useSimulationEnMasse";
import LicensePlate from "../../../../common/LicensePlate/LicensePlate";
import styles from "./SimulationEnMasse.module.css";

const SimulationEnMasse = ({ data, onComplete }) => {
  const {
    formData,
    simulationResults,
    isSimulating,
    handleInputChange,
    handleSubmit,
    getOriginalValue,
    calculateDifference,
    calculatePercentage,
    formatValue,
    getStatusIcon,
  } = useSimulationEnMasse(data, { onComplete });

  const renderComparisonCell = useCallback(
    (result, fieldKey, simulatedValue) => {
      const originalValue = getOriginalValue(result, fieldKey);
      const difference = calculateDifference(simulatedValue, originalValue);
      const percentage = calculatePercentage(simulatedValue, originalValue);

      const formattedSimulated = formatValue(simulatedValue, fieldKey);
      const formattedOriginal = originalValue
        ? formatValue(originalValue, fieldKey)
        : null;
      const formattedDifference = difference
        ? formatValue(Math.abs(difference), fieldKey)
        : null;

      const isPositive = difference > 0;
      const isNegative = difference < 0;
      const isEqual = difference === 0;

      return (
        <div className={styles.simulationCell}>
          <div className={styles.simulationValue}>{formattedSimulated}</div>

          {originalValue && (
            <>
              <div className={styles.originalValue}>
                Initial: {formattedOriginal}
              </div>

              {difference !== null && !isEqual && (
                <div className={styles.differenceContainer}>
                  <div
                    className={`${styles.differenceValue} ${
                      isPositive
                        ? styles.differencePositive
                        : isNegative
                          ? styles.differenceNegative
                          : styles.differenceNeutral
                    }`}
                  >
                    <i
                      className={`fas ${
                        isPositive
                          ? "fa-arrow-up"
                          : isNegative
                            ? "fa-arrow-down"
                            : "fa-equals"
                      } ${styles.trendIcon} ${
                        isPositive
                          ? styles.trendUp
                          : isNegative
                            ? styles.trendDown
                            : styles.trendEqual
                      }`}
                    ></i>
                    {isPositive ? "+" : ""}
                    {formattedDifference}
                  </div>

                  {percentage !== null && (
                    <span
                      className={`${styles.percentageBadge} ${
                        isPositive
                          ? styles.percentagePositive
                          : isNegative
                            ? styles.percentageNegative
                            : styles.percentageNeutral
                      }`}
                    >
                      {isPositive ? "+" : ""}
                      {percentage.toFixed(1)}%
                    </span>
                  )}
                </div>
              )}

              {isEqual && (
                <div className={styles.differenceContainer}>
                  <div
                    className={`${styles.differenceValue} ${styles.differenceNeutral}`}
                  >
                    <i
                      className={`fas fa-equals ${styles.trendIcon} ${styles.trendEqual}`}
                    ></i>
                    Identique
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      );
    },
    [getOriginalValue, calculateDifference, calculatePercentage, formatValue],
  );

  return (
    <div className="simulation-en-masse">
      <div className="modal-header">
        <h4 className="modal-title">
          <i className="fas fa-calculator mr-2"></i>
          Simulation en masse ({data.length} véhicules)
        </h4>
      </div>

      <div className="modal-body">
        {/* Formulaire */}
        <form onSubmit={handleSubmit} className="mb-4">
          <div className="card">
            <div className="card-body">
              <h6 className="card-title mb-3">
                <i className="fas fa-cogs mr-2"></i>
                Paramètres de simulation
              </h6>
              <div className="row">
                <div className="col-md-6">
                  <div className="form-group">
                    <label htmlFor="duree" className="form-label">
                      Durée (en mois) *
                    </label>
                    <input
                      type="number"
                      id="duree"
                      name="duree"
                      className="form-control"
                      value={formData.duree}
                      onChange={handleInputChange}
                      min="1"
                      disabled={isSimulating}
                      placeholder="Ex: 48"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="form-group">
                    <label className="form-label d-block">
                      Méthode de calcul
                    </label>
                    <div className="d-flex" style={{ gap: 16 }}>
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          id="simulation"
                          name="simulationType"
                          className="custom-control-input"
                          value="simulation"
                          checked={formData.simulationType === "simulation"}
                          onChange={handleInputChange}
                          disabled={isSimulating}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="simulation"
                        >
                          Simulation normale
                        </label>
                      </div>
                      <div className="custom-control custom-radio">
                        <input
                          type="radio"
                          id="simulation_prorata_km"
                          name="simulationType"
                          className="custom-control-input"
                          value="simulation_prorata_km"
                          checked={
                            formData.simulationType === "simulation_prorata_km"
                          }
                          onChange={handleInputChange}
                          disabled={isSimulating}
                        />
                        <label
                          className="custom-control-label"
                          htmlFor="simulation_prorata_km"
                        >
                          Prorata km réel
                        </label>
                      </div>
                    </div>
                    <small className="text-muted mt-1 d-block">
                      {formData.simulationType === "simulation"
                        ? "Le kilométrage est projeté à partir du km initial du contrat."
                        : "Le kilométrage est projeté à partir du km réel actualisé du véhicule."}
                    </small>
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isSimulating || !formData.duree}
                >
                  {isSimulating ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Simulation en cours...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-play mr-2"></i>
                      Démarrer la simulation
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>

        {/* Tableau des résultats */}
        <div className="card">
          <div className="card-body">
            <h6 className="card-title mb-3">
              <i className="fas fa-table mr-2"></i>
              Résultats de la simulation
            </h6>
            <div className="table-responsive">
              <table className="table table-hover">
                <thead className="table-light">
                  <tr>
                    <th scope="col">Statut</th>
                    <th scope="col">Véhicule</th>
                    <th scope="col">Client</th>
                    <th scope="col">Dernier mouvement</th>
                    <th scope="col">Valeur théorique</th>
                    <th scope="col">Indemnités</th>
                    <th scope="col">Montant VR</th>
                    <th scope="col">Km sup.</th>
                  </tr>
                </thead>
                <tbody>
                  {simulationResults.map((result, index) => (
                    <tr key={result.vehicleId}>
                      <td className="text-center">
                        {getStatusIcon(result.status)}
                      </td>
                      <td>
                        <div>
                          <strong>
                            {result.vehicleInfo.marque}{" "}
                            {result.vehicleInfo.modele}
                          </strong>
                          <br />
                          <LicensePlate
                            value={result.vehicleInfo.immatriculation}
                            size="sm"
                          />
                        </div>
                      </td>
                      <td>
                        <small className="text-xs">
                          {result.vehicleInfo.client || "N/A"}
                        </small>
                      </td>
                      <td>
                        <div>
                          <div className="mb-1">
                            <strong>{result.lastMovement.type}</strong>
                            <br />
                            <small className="text-muted">
                              {new Date(
                                result.lastMovement.date,
                              ).toLocaleDateString()}
                            </small>
                          </div>
                          <div className="d-flex " style={{ gap: 5 }}>
                            <span
                              className="badge bg-light border"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {result.lastMovement.duree
                                ? result.lastMovement.duree + " mois"
                                : "?? mois"}
                            </span>
                            <span
                              className="badge bg-light border"
                              style={{ fontSize: "0.7rem" }}
                            >
                              {result.lastMovement.kilometrage
                                ? new Intl.NumberFormat("fr-FR").format(
                                    result.lastMovement.kilometrage,
                                  ) + " km"
                                : "?? km"}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td>
                        {result.status === "success" && result.simulation ? (
                          renderComparisonCell(
                            result,
                            "valeur_theorique",
                            result.simulation.valeur_theorique,
                          )
                        ) : result.status === "error" ? (
                          <div
                            className={
                              styles.simulationCell + " " + styles.errorCell
                            }
                          >
                            <i className="fas fa-exclamation-triangle"></i>
                            <small>Erreur</small>
                          </div>
                        ) : (
                          <div
                            className={`${styles.simulationCell} ${styles.loadingCell}`}
                          >
                            <span className="text-muted">En attente...</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {result.status === "success" && result.simulation ? (
                          renderComparisonCell(
                            result,
                            "indemnites",
                            result.simulation.indemnites,
                          )
                        ) : result.status === "error" ? (
                          <div
                            className={
                              styles.simulationCell + " " + styles.errorCell
                            }
                          >
                            <i className="fas fa-exclamation-triangle"></i>
                            <small>Erreur</small>
                          </div>
                        ) : (
                          <div
                            className={`${styles.simulationCell} ${styles.loadingCell}`}
                          >
                            <span className="text-muted">En attente...</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {result.status === "success" && result.simulation ? (
                          renderComparisonCell(
                            result,
                            "montant_vr",
                            result.simulation.montant_vr,
                          )
                        ) : result.status === "error" ? (
                          <div
                            className={
                              styles.simulationCell + " " + styles.errorCell
                            }
                          >
                            <i className="fas fa-exclamation-triangle"></i>
                            <small>Erreur</small>
                          </div>
                        ) : (
                          <div
                            className={`${styles.simulationCell} ${styles.loadingCell}`}
                          >
                            <span className="text-muted">En attente...</span>
                          </div>
                        )}
                      </td>
                      <td>
                        {result.status === "success" && result.simulation ? (
                          renderComparisonCell(
                            result,
                            "montant_kilometres_supplementaires",
                            result.simulation
                              .montant_kilometres_supplementaires,
                          )
                        ) : result.status === "error" ? (
                          <div
                            className={
                              styles.simulationCell + " " + styles.errorCell
                            }
                            title={result.error}
                          >
                            <i className="fas fa-exclamation-triangle"></i>
                            <small>Erreur</small>
                          </div>
                        ) : (
                          <div
                            className={`${styles.simulationCell} ${styles.loadingCell}`}
                          >
                            <span className="text-muted">En attente...</span>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SimulationEnMasse;
